'use client';

import { Button, Card, Flex, IconButton, Stack, Text } from '@chakra-ui/react';
import EditDeleteActions from '@components/common/EditDeleteActions';
import ExpandableSearchButton from '@components/common/ExpandableSearchButton';
import NewMerchandiseDialog, {
  MerchandiseDraftRow,
} from '@features/positions/merchandise/NewMerchandiseDialog';
import { formatDate } from 'helpers/dates';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuPackage } from 'react-icons/lu';
import { FilterIcon, PlusIcon } from 'vectors';

interface MerchandiseItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  category: string;
  updatedAt: string;
}

const fakeMerchandise: MerchandiseItem[] = [
  {
    id: 'm-1',
    name: 'Raw Shea Butter (Grade A)',
    quantity: 2500,
    unit: 'kg',
    category: 'Oils, fats & extracts',
    updatedAt: '2026-05-20T08:00:00Z',
  },
  {
    id: 'm-2',
    name: 'Cocoa Beans (Fermented)',
    quantity: 1800,
    unit: 'kg',
    category: 'Coffee, tea, cocoa & spices',
    updatedAt: '2026-05-19T10:30:00Z',
  },
  {
    id: 'm-3',
    name: 'Cashew Nuts (Raw)',
    quantity: 600,
    unit: 'kg',
    category: 'Nuts & seeds',
    updatedAt: '2026-05-18T14:00:00Z',
  },
  {
    id: 'm-4',
    name: 'Palm Kernel Oil',
    quantity: 400,
    unit: 'liters',
    category: 'Oils, fats & extracts',
    updatedAt: '2026-05-15T09:00:00Z',
  },
  {
    id: 'm-5',
    name: 'Woven Cotton Fabric',
    quantity: 1200,
    unit: 'm',
    category: 'Textiles, yarn & fabrics',
    updatedAt: '2026-05-12T11:00:00Z',
  },
];

interface MerchandiseCardProps {
  item: MerchandiseItem;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const MerchandiseCard = memo(({ item, onEdit, onDelete }: MerchandiseCardProps) => (
  <Card.Root w="full" borderColor="border.light" borderRadius="xl" bg="surface.background">
    <Card.Body p={0}>
      <Flex alignItems="center" gap={3} px={5} py={4}>
        <Flex
          bg="surface.hover"
          borderWidth="1px"
          borderColor="border.light"
          borderRadius="lg"
          p={2.5}
          flexShrink={0}
        >
          <LuPackage size={18} />
        </Flex>
        <Flex direction="column" flex={1} minW={0}>
          <Text fontSize="md" fontWeight="medium" truncate>
            {item.name}
          </Text>
          <Text fontSize="xs" color="text.secondary">
            {item.category} · Updated {formatDate(item.updatedAt)}
          </Text>
        </Flex>
        <Flex direction="column" alignItems="flex-end" flexShrink={0}>
          <Text fontSize="lg" fontWeight="semibold">
            {item.quantity.toLocaleString()}
          </Text>
          <Text fontSize="xs" color="text.secondary">
            {item.unit}
          </Text>
        </Flex>
        <EditDeleteActions id={item.id} onEdit={onEdit} onDelete={onDelete} />
      </Flex>
    </Card.Body>
  </Card.Root>
));
MerchandiseCard.displayName = 'MerchandiseCard';

const MyMerchandisePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingRows, setEditingRows] = useState<MerchandiseDraftRow[] | null>(null);

  const onClickNew = useCallback(() => {
    setIsCreateOpen(true);
  }, []);

  const onCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
  }, []);

  const onEditItem = useCallback((id: string) => {
    const item = fakeMerchandise.find((m) => m.id === id);
    if (item == null) {
      return;
    }
    setEditingRows([
      {
        id: item.id,
        name: item.name,
        quantity: String(item.quantity),
        unit: item.unit,
        category: item.category,
      },
    ]);
  }, []);

  const onCloseEdit = useCallback(() => {
    setEditingRows(null);
  }, []);

  const onDeleteItem = useCallback((_id: string) => {}, []);

  const filteredMerchandise = useMemo(() => {
    if (searchQuery === '') {
      return fakeMerchandise;
    }
    const q = searchQuery.toLowerCase();
    return fakeMerchandise.filter(
      (m) => m.name.toLowerCase().includes(q) || m.category.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  return (
    <Flex flexDirection="column" h="full" p={4}>
      <Flex borderBottomWidth="1px" borderColor="border.light" pb={3} alignItems="center">
        <Flex ml="auto" gap={2} alignItems="center">
          <ExpandableSearchButton
            query={searchQuery}
            setQuery={setSearchQuery}
            size="xs"
            iconColor="text.secondary"
          />
          <IconButton variant="ghost" size="xs" borderRadius="md">
            <FilterIcon color="text.secondary" />
          </IconButton>
          <Button size="2xs" borderRadius="md" onClick={onClickNew}>
            New merchandise <PlusIcon />
          </Button>
        </Flex>
      </Flex>
      <Stack gap={3} pt={4}>
        {filteredMerchandise.map((item) => (
          <MerchandiseCard
            key={item.id}
            item={item}
            onEdit={onEditItem}
            onDelete={onDeleteItem}
          />
        ))}
      </Stack>
      <NewMerchandiseDialog open={isCreateOpen} onClose={onCloseCreate} />
      <NewMerchandiseDialog
        open={editingRows != null}
        onClose={onCloseEdit}
        initialRows={editingRows ?? undefined}
        title="Edit merchandise"
      />
    </Flex>
  );
};

export default memo(MyMerchandisePage);
